import { useState, useEffect, useRef } from "react"
import type { KeyboardEvent } from "react"
import { useForm, Controller } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Plus, X, Loader2, AlertCircle, Image as ImageIcon, Upload, CheckCircle, Trash2, MoreVertical, Edit2, ChevronDown, Check } from "lucide-react"
import { toast } from "sonner"
import { cn } from "@/lib/utils"
import { useCreateProduct } from "@/hooks/seller/useCreateProduct"
import { useUpdateProduct } from "@/hooks/seller/useUpdateProduct"
import { useUploadProductImage } from "@/hooks/seller/useUploadProductImage"
import { useDeleteProductImage } from "@/hooks/seller/useDeleteProductImage"
import { useUpdateProductImage } from "@/hooks/seller/useUpdateProductImage"
import { useProductCategoriesWithSubs } from "@/hooks/seller/useProductCategoriesWithSubs"
import { productSchema, type ProductFormValues } from "@/lib/schemas/product.schema"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import type { Product, ProductImage } from "@/lib/api/product.api"

export const inputCls =
  "w-full px-3 py-2 bg-slate-50 border-[0.5px] border-slate-200/60 rounded-lg text-xs text-[#291716] focus:border-[#be001e] focus:ring-0 outline-none transition-colors placeholder:text-slate-400"

export function Field({
  id, label, error, full, children,
}: {
  id: string; label: string; error?: string; full?: boolean; children: React.ReactNode
}) {
  return (
    <div className={`flex flex-col gap-1.5 ${full ? "sm:col-span-2" : ""}`}>
      <Label htmlFor={id} className="text-[10px] font-bold uppercase tracking-wider text-[#5d3f3d]">
        {label}
      </Label>
      {children}
      {error && <p className="text-xs text-[#be001e] font-semibold">{error}</p>}
    </div>
  )
}

export function AddProductForm({
  companyId,
  productToEdit,
  onClose,
}: {
  companyId: string
  productToEdit?: Product
  onClose: () => void
}) {
  const createProduct = useCreateProduct(companyId)
  const updateProduct = useUpdateProduct(productToEdit?.id || "")
  const uploadImageMutation = useUploadProductImage(productToEdit?.id || "")
  const deleteImageMutation = useDeleteProductImage(productToEdit?.id || "")
  const [deletingImageId, setDeletingImageId] = useState<string | null>(null)
  const productImages = productToEdit?.images || []
  const [tagInput, setTagInput] = useState("")

  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [imageCaption, setImageCaption] = useState("")
  const [imageOrder, setImageOrder] = useState<number | "">("")
  const [uploadSuccessMsg, setUploadSuccessMsg] = useState<string | null>(null)
  const [showUploadImageForm, setShowUploadImageForm] = useState(false)
  const [showImageMenu, setShowImageMenu] = useState(false)
  const updateImageMutation = useUpdateProductImage(productToEdit?.id || "")
  const [isUpdateMode, setIsUpdateMode] = useState(false)
  const [selectedUpdateImageId, setSelectedUpdateImageId] = useState<string | null>(null)
  const [isDeleteMode, setIsDeleteMode] = useState(false)
  const [selectedImageId, setSelectedImageId] = useState<string | null>(null)
  const [updateCaption, setUpdateCaption] = useState("")
  const [updateOrder, setUpdateOrder] = useState<number | "">("")
  const [updateSuccessMsg, setUpdateSuccessMsg] = useState<string | null>(null)

  const handleSelectImageForUpdate = (img: ProductImage) => {
    if (selectedUpdateImageId === img.id) {
      setSelectedUpdateImageId(null)
      setUpdateCaption("")
      setUpdateOrder("")
    } else {
      setSelectedUpdateImageId(img.id)
      setUpdateCaption(img.caption || "")
      setUpdateOrder(img.order !== undefined && img.order !== null ? img.order : "")
    }
    setUpdateSuccessMsg(null)
  }

  const handleSaveUpdateImage = () => {
    if (!productToEdit?.id || !selectedUpdateImageId) return
    setUpdateSuccessMsg(null)
    updateImageMutation.mutate(
      { imageId: selectedUpdateImageId, caption: updateCaption, order: updateOrder },
      {
        onSuccess: () => {
          setUploadSuccessMsg(null)
          setSelectedUpdateImageId(null)
          setUpdateCaption("")
          setUpdateOrder("")
          setIsUpdateMode(false)
        },
      }
    )
  }

  const handleDeleteImage = (imageId: string) => {
    if (!productToEdit?.id) return
    setDeletingImageId(imageId)
    deleteImageMutation.mutate(imageId, {
      onSettled: () => setDeletingImageId(null),
    })
  }

  const handleSingleDeleteImage = () => {
    if (!productToEdit?.id || !selectedImageId) return
    deleteImageMutation.mutate(selectedImageId, {
      onSuccess: () => {
        setSelectedImageId(null)
        setIsDeleteMode(false)
      },
    })
  }

  const handleUploadImage = () => {
    if (!selectedFile || !productToEdit?.id) return
    setUploadSuccessMsg(null)
    uploadImageMutation.mutate(
      { file: selectedFile, caption: imageCaption, order: imageOrder },
      {
        onSuccess: () => {
          setSelectedFile(null)
          setImageCaption("")
          setImageOrder("")
          setShowUploadImageForm(false)
        },
      }
    )
  }

  const { register, handleSubmit, control, formState: { errors }, watch, setValue } =
    useForm<ProductFormValues>({
      mode: "onChange",
      resolver: zodResolver(productSchema) as any,
      defaultValues: productToEdit ? {
        productName: productToEdit.productName,
        description: productToEdit.description || "",
        productCode: productToEdit.productCode || "",
        productCategory: productToEdit.productCategory || "",
        productSubCategory: productToEdit.productSubCategory || "",
        tags: productToEdit.tags || []
      } : { productName: "", description: "", productCode: "", productCategory: "", productSubCategory: "", tags: [] },
    })

  useEffect(() => {
    if (productToEdit) {
      setValue("productName", productToEdit.productName)
      setValue("description", productToEdit.description || "")
      setValue("productCode", productToEdit.productCode || "")
      setValue("productCategory", productToEdit.productCategory || "")
      setValue("productSubCategory", productToEdit.productSubCategory || "")
      setValue("tags", productToEdit.tags || [])
    }
  }, [productToEdit, setValue])

  const tags = watch("tags") ?? []

  const addTag = () => {
    const t = tagInput.trim()
    if (t && !tags.includes(t) && tags.length < 20) {
      setValue("tags", [...tags, t], { shouldValidate: true })
      setTagInput("")
    }
  }

  const removeTag = (tag: string) =>
    setValue("tags", tags.filter((t) => t !== tag), { shouldValidate: true })

  const handleTagKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addTag() }
  }

  const { data: categoryList = [], isLoading: loadingCategories } = useProductCategoriesWithSubs()
  const [isCategoryOpen, setIsCategoryOpen] = useState(false)
  const [isSubCategoryOpen, setIsSubCategoryOpen] = useState(false)
  const categoryRef = useRef<HTMLDivElement>(null)
  const subCategoryRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (categoryRef.current && !categoryRef.current.contains(e.target as Node)) {
        setIsCategoryOpen(false)
      }
      if (subCategoryRef.current && !subCategoryRef.current.contains(e.target as Node)) {
        setIsSubCategoryOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const categoryValue = watch("productCategory") || ""
  const subCategoryValue = watch("productSubCategory") || ""

  const selectedCategoryObj = categoryList.find(
    (c) => c.name.toLowerCase() === categoryValue.toLowerCase()
  )
  const availableSubCategories = selectedCategoryObj?.subCategories || []

  useEffect(() => {
    register("productCategory")
    register("productSubCategory")
  }, [register])

  const handleSelectCategory = (catName: string) => {
    setValue("productCategory", catName, { shouldValidate: true })
    if (catName !== categoryValue) {
      setValue("productSubCategory", "", { shouldValidate: false })
    }
    setIsCategoryOpen(false)
  }

  const handleSubCategoryClick = () => {
    if (!categoryValue) {
      toast.error("Please select a Category first")
      return
    }
    setIsSubCategoryOpen((prev) => !prev)
  }

  const handleSelectSubCategory = (subCatName: string) => {
    setValue("productSubCategory", subCatName, { shouldValidate: true })
    setIsSubCategoryOpen(false)
  }

  const onInvalidSubmit = () => {
    const cat = watch("productCategory")
    const subCat = watch("productSubCategory")
    if (!cat) {
      toast.error("Category is required")
    }
    if (!subCat) {
      toast.error("Sub-Category is required")
    }
  }

  const onSubmit = (values: ProductFormValues) => {
    let hasError = false
    if (!values.productCategory) {
      toast.error("Category is required")
      hasError = true
    }
    if (!values.productSubCategory) {
      toast.error("Sub-Category is required")
      hasError = true
    }
    if (hasError) return

    if (productToEdit) {
      updateProduct.mutate(values, {
        onSuccess: () => {
          toast.success("Product Updated Successfully")
          onClose()
        },
      })
    } else {
      createProduct.mutate(values, {
        onSuccess: () => {
          toast.success("Product Added Successfully")
          onClose()
        },
      })
    }
  }

  const isPending = productToEdit ? updateProduct.isPending : createProduct.isPending
  const isError = productToEdit ? updateProduct.isError : createProduct.isError
  const error = productToEdit ? updateProduct.error : createProduct.error

  return (
    <section className="bg-[#be001e05] border border-[#be001e] rounded-xl p-5 mb-6 space-y-4 shadow-[0_8px_30px_rgba(190,0,30,0.06)] animate-in fade-in duration-200">
      <div className="flex items-center justify-between pb-3 border-b border-[#be001e1a]">
        <div className="flex items-center gap-1.5">
          <h2 className="text-sm font-black  tracking-wider text-[#be001e]">{productToEdit ? "Edit Product" : "Add New Product"}</h2>
        </div>
        <button onClick={onClose} className="p-1 text-[#be001e] hover:bg-[#be001e0d] rounded-full transition-colors cursor-pointer">
          <X className="size-4" />
        </button>
      </div>

      <form onSubmit={handleSubmit(onSubmit as any, onInvalidSubmit)} noValidate className="space-y-4">
        <Field id="productName" label="Product Name *" error={errors.productName?.message} full>
          <Input id="productName" placeholder="e.g. Dimension Analyzer" {...register("productName")} className={inputCls} />
        </Field>

        <Field id="description" label="Description" error={errors.description?.message} full>
          <textarea
            id="description"
            rows={3}
            placeholder="Brief product description…"
            {...register("description")}
            className="w-full resize-none rounded-lg border-[0.5px] border-slate-200/60 bg-slate-50 px-3 py-2.5 text-xs text-[#291716] outline-none placeholder:text-slate-400 focus:border-[#be001e]"
          />
        </Field>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field id="productCode" label="Product Code" error={errors.productCode?.message}>
            <Input id="productCode" placeholder="e.g. SKU-001" {...register("productCode")} className={inputCls} />
          </Field>

          <Field id="productCategory" label="Category *" error={errors.productCategory?.message}>
            <div ref={categoryRef} className="relative">
              <button
                id="productCategory"
                type="button"
                onClick={() => setIsCategoryOpen((prev) => !prev)}
                className={cn(
                  inputCls,
                  "flex items-center justify-between text-left cursor-pointer",
                  errors.productCategory && "border-[#be001e]",
                  isCategoryOpen && "border-[#be001e] ring-1 ring-[#be001e]/20"
                )}
              >
                <span className={categoryValue ? "text-[#291716] font-medium truncate" : "text-slate-400"}>
                  {categoryValue || (loadingCategories ? "Loading categories..." : "Select Category")}
                </span>
                <ChevronDown className={cn("size-3.5 text-slate-400 shrink-0 ml-2 transition-transform duration-200", isCategoryOpen && "rotate-180 text-[#be001e]")} />
              </button>

              {isCategoryOpen && (
                <div className="absolute top-full left-0 right-0 mt-1.5 z-50 bg-white border border-slate-200 rounded-lg shadow-xl max-h-[160px] overflow-y-auto divide-y divide-slate-100 animate-in fade-in slide-in-from-top-1 duration-150">
                  {categoryList.length === 0 ? (
                    <div className="p-3 text-xs text-slate-400 text-center">
                      {loadingCategories ? "Loading categories..." : "No categories available"}
                    </div>
                  ) : (
                    categoryList.map((cat) => (
                      <button
                        key={cat.name}
                        type="button"
                        onClick={() => handleSelectCategory(cat.name)}
                        className={cn(
                          "w-full h-10 px-3 py-2 text-left text-xs font-medium transition-colors hover:bg-slate-50 flex items-center justify-between cursor-pointer",
                          categoryValue === cat.name ? "bg-[#be001e]/5 text-[#be001e] font-bold" : "text-slate-700"
                        )}
                      >
                        <span className="truncate pr-2">{cat.name}</span>
                        {categoryValue === cat.name && <Check className="size-3.5 text-[#be001e] shrink-0" />}
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          </Field>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field id="productSubCategory" label="Sub-Category *" error={errors.productSubCategory?.message}>
            <div ref={subCategoryRef} className="relative">
              <button
                id="productSubCategory"
                type="button"
                onClick={handleSubCategoryClick}
                className={cn(
                  inputCls,
                  "flex items-center justify-between text-left cursor-pointer",
                  !categoryValue && "opacity-75 bg-slate-100/70",
                  errors.productSubCategory && "border-[#be001e]",
                  isSubCategoryOpen && "border-[#be001e] ring-1 ring-[#be001e]/20"
                )}
              >
                <span className={subCategoryValue ? "text-[#291716] font-medium truncate" : "text-slate-400"}>
                  {subCategoryValue || (categoryValue ? "Select Sub-Category" : "Select Category first")}
                </span>
                <ChevronDown className={cn("size-3.5 text-slate-400 shrink-0 ml-2 transition-transform duration-200", isSubCategoryOpen && "rotate-180 text-[#be001e]")} />
              </button>

              {isSubCategoryOpen && (
                <div className="absolute top-full left-0 right-0 mt-1.5 z-50 bg-white border border-slate-200 rounded-lg shadow-xl max-h-[160px] overflow-y-auto divide-y divide-slate-100 animate-in fade-in slide-in-from-top-1 duration-150">
                  {availableSubCategories.length === 0 ? (
                    <div className="p-3 text-xs text-slate-400 text-center">
                      No sub-categories available
                    </div>
                  ) : (
                    availableSubCategories.map((sub) => (
                      <button
                        key={sub}
                        type="button"
                        onClick={() => handleSelectSubCategory(sub)}
                        className={cn(
                          "w-full h-10 px-3 py-2 text-left text-xs font-medium transition-colors hover:bg-slate-50 flex items-center justify-between cursor-pointer",
                          subCategoryValue === sub ? "bg-[#be001e]/5 text-[#be001e] font-bold" : "text-slate-700"
                        )}
                      >
                        <span className="truncate pr-2">{sub}</span>
                        {subCategoryValue === sub && <Check className="size-3.5 text-[#be001e] shrink-0" />}
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          </Field>

          <Controller
            control={control}
            name="tags"
            render={() => (
              <Field id="tag-input" label="Tags" error={errors.tags?.message}>
                <div className="relative flex items-center">
                  <input
                    id="tag-input"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={handleTagKeyDown}
                    placeholder='Type & press Enter'
                    className="w-full pl-3 pr-20 py-2 bg-slate-50 border-[0.5px] border-slate-200/60 rounded-lg text-xs text-[#291716] focus:border-[#be001e] outline-none transition-colors placeholder:text-slate-400"
                  />
                  <button type="button" onClick={addTag} className="absolute right-1 px-4 py-1 bg-[#fff0ef] text-[#be001e] font-bold text-xs border-[0.5px] border-[#e7bdb9]/40 rounded-md hover:bg-[#be001e]/5 transition-colors cursor-pointer">
                    Add
                  </button>
                </div>
                {tags.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {tags.map((t) => (
                      <span key={t} className="flex items-center gap-1 rounded-full border border-[#e7bdb9]/50 bg-[#fff0ef] px-2 py-0.5 text-[10px] font-semibold text-[#be001e]">
                        {t}
                        <button type="button" onClick={() => removeTag(t)} className="hover:text-red-800 cursor-pointer"><X className="size-2.5" /></button>
                      </span>
                    ))}
                  </div>
                )}
              </Field>
            )}
          />
        </div>
            
        {productToEdit && (
          <div className="pt-4 border-t border-[#be001e1a] space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <ImageIcon className="size-4 text-[#be001e]" />
                <h3 className="text-xs font-black uppercase tracking-wider text-[#be001e]">Product Images</h3>
              </div>
              <div className="flex items-center gap-2">
                {isDeleteMode ? (
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-bold text-red-600 flex items-center gap-1 bg-red-50 px-2 py-0.5 rounded border border-red-200">
                      <Trash2 className="size-3" /> Select image to delete
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        setIsDeleteMode(false)
                        setSelectedImageId(null)
                      }}
                      className="px-2 py-0.5 text-slate-500 hover:text-slate-700 text-[10px] font-bold rounded border border-slate-200 hover:bg-slate-50 cursor-pointer"
                    >
                      Cancel
                    </button>
                  </div>
                ) : isUpdateMode ? (
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-bold text-[#be001e] flex items-center gap-1 bg-[#fff0ef] px-2 py-0.5 rounded border border-[#e7bdb9]/50">
                      <Edit2 className="size-3" /> Select image to update detail
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        setIsUpdateMode(false)
                        setSelectedUpdateImageId(null)
                        setUpdateCaption("")
                        setUpdateOrder("")
                      }}
                      className="px-2 py-0.5 text-slate-500 hover:text-slate-700 text-[10px] font-bold rounded border border-slate-200 hover:bg-slate-50 cursor-pointer"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <>
                    {productImages && productImages.length > 0 && (
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                        {productImages.length} {productImages.length === 1 ? 'image' : 'images'}
                      </span>
                    )}
                    
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setShowImageMenu((prev) => !prev)}
                        className="p-1.5 text-slate-600 hover:text-[#be001e] hover:bg-[#fff0ef] rounded-lg border border-slate-200 hover:border-[#e7bdb9] transition-all cursor-pointer shadow-sm"
                        title="Image options"
                      >
                        <MoreVertical className="size-3.5" />
                      </button>

                      {showImageMenu && (
                        <>
                          <div
                            className="fixed inset-0 z-40"
                            onClick={() => setShowImageMenu(false)}
                          />
                          <div className="absolute right-0 top-full mt-1.5 w-44 bg-white border border-slate-200 rounded-xl shadow-xl z-50 py-1 font-sans text-xs animate-in fade-in zoom-in-95 duration-150">
                            <button
                              type="button"
                              onClick={() => {
                                setShowUploadImageForm(true)
                                setIsDeleteMode(false)
                                setIsUpdateMode(false)
                                setShowImageMenu(false)
                              }}
                              className="w-full px-3 py-2 text-left text-slate-700 hover:bg-[#fff0ef] hover:text-[#be001e] font-bold flex items-center gap-2 transition-colors cursor-pointer"
                            >
                              <Plus className="size-3.5 text-[#be001e]" /> Add New Image
                            </button>
                            {productImages.length > 0 && (
                              <>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setIsUpdateMode(true)
                                    setIsDeleteMode(false)
                                    setShowUploadImageForm(false)
                                    setSelectedUpdateImageId(null)
                                    setShowImageMenu(false)
                                  }}
                                  className="w-full px-3 py-2 text-left text-slate-700 hover:bg-[#fff0ef] hover:text-[#be001e] font-bold flex items-center gap-2 transition-colors cursor-pointer border-t border-slate-100"
                                >
                                  <Edit2 className="size-3.5 text-[#be001e]" /> Update Detail
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setIsDeleteMode(true)
                                    setIsUpdateMode(false)
                                    setShowUploadImageForm(false)
                                    setSelectedImageId(null)
                                    setShowImageMenu(false)
                                  }}
                                  className="w-full px-3 py-2 text-left text-slate-700 hover:bg-[#fff0ef] hover:text-[#be001e] font-bold flex items-center gap-2 transition-colors cursor-pointer border-t border-slate-100"
                                >
                                  <Trash2 className="size-3.5 text-[#be001e]" /> Delete Image
                                </button>
                              </>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Existing Images Gallery Preview */}
            {productImages.length > 0 && (
              <div className="space-y-2">
                <div className="flex flex-wrap gap-3 p-3 bg-[#be001e]/[0.02] border border-[#be001e]/15 rounded-xl shadow-sm">
                  {productImages.map((img) => {
                    const isDeleteSelected = selectedImageId === img.id
                    const isUpdateSelected = selectedUpdateImageId === img.id
                    const isSelected = isDeleteMode ? isDeleteSelected : isUpdateMode ? isUpdateSelected : false

                    return (
                      <div
                        key={img.id}
                        onClick={() => {
                          if (isDeleteMode) {
                            setSelectedImageId((prev) => (prev === img.id ? null : img.id))
                          } else if (isUpdateMode) {
                            handleSelectImageForUpdate(img)
                          }
                        }}
                        className={`relative group/img flex flex-col items-center select-none ${
                          isDeleteMode || isUpdateMode ? "cursor-pointer" : ""
                        }`}
                      >
                        <div className={`w-20 h-20 rounded-lg overflow-hidden border bg-slate-50 shadow-sm relative group/imgbox transition-all ${
                          isSelected ? "ring-2 ring-[#be001e] border-[#be001e]" : "border-slate-200"
                        }`}>
                          <img
                            src={img.url}
                            alt={img.caption || "Product image"}
                            className="w-full h-full object-cover group-hover/img:scale-105 transition-transform duration-200"
                          />
                          
                          {/* Checkbox overlay in Delete Mode or Update Mode */}
                          {isDeleteMode || isUpdateMode ? (
                            <div className="absolute top-1 left-1 bg-white/90 p-0.5 rounded shadow">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => {
                                  e.stopPropagation()
                                  if (isDeleteMode) {
                                    setSelectedImageId((prev) => (prev === img.id ? null : img.id))
                                  } else if (isUpdateMode) {
                                    handleSelectImageForUpdate(img)
                                  }
                                }}
                                className="size-4 rounded accent-[#be001e] cursor-pointer block"
                              />
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation()
                                handleDeleteImage(img.id)
                              }}
                              disabled={deleteImageMutation.isPending}
                              className="absolute top-1 right-1 p-1 bg-red-600/90 hover:bg-red-700 text-white rounded-md opacity-0 group-hover/imgbox:opacity-100 transition-opacity cursor-pointer shadow-sm disabled:opacity-50"
                              title="Delete image"
                            >
                              {deleteImageMutation.isPending && deletingImageId === img.id ? (
                                <Loader2 className="size-3 animate-spin" />
                              ) : (
                                <Trash2 className="size-3" />
                              )}
                            </button>
                          )}
                        </div>
                        {(img.caption || (img.order !== undefined && img.order !== null)) && (
                          <div className="mt-1 flex flex-col items-center max-w-[80px] leading-tight">
                            {img.caption && (
                              <span className="text-[11px] font-bold text-slate-700 truncate w-full text-center" title={img.caption}>
                                {img.caption}
                              </span>
                            )}
                            {img.order !== undefined && img.order !== null && (
                              <span className="text-[10px] font-semibold text-slate-500">
                                Order: {img.order}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>

                {/* Single Image Delete Action Footer */}
                {isDeleteMode && (
                  <div className="flex items-center justify-between p-2.5 bg-red-50/60 border border-red-100 rounded-xl">
                    <span className="text-xs text-slate-700 font-bold">
                      {selectedImageId ? "1 image selected" : "Select an image to delete"}
                    </span>
                    <button
                      type="button"
                      onClick={handleSingleDeleteImage}
                      disabled={!selectedImageId || deleteImageMutation.isPending}
                      className="px-3.5 py-1.5 bg-[#be001e] hover:bg-[#a50019] text-white font-bold text-xs rounded-lg transition-all flex items-center gap-1.5 cursor-pointer disabled:opacity-50 shadow-sm"
                    >
                      {deleteImageMutation.isPending ? <Loader2 className="size-3.5 animate-spin" /> : <Trash2 className="size-3.5" />}
                      {deleteImageMutation.isPending ? "Deleting..." : "Delete Selected Image"}
                    </button>
                  </div>
                )}

                {/* Update Detail Inline Form */}
                {isUpdateMode && selectedUpdateImageId && (
                  <div className="bg-white border-[0.5px] border-slate-200/80 rounded-xl p-4 space-y-3 shadow-sm animate-in fade-in duration-200 mt-2">
                    <div className="flex items-center justify-between">
                      <h4 className="text-[10px] font-extrabold uppercase tracking-wider text-[#5d3f3d]">
                        Update Image Details
                      </h4>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedUpdateImageId(null)
                          setUpdateCaption("")
                          setUpdateOrder("")
                          setIsUpdateMode(false)
                        }}
                        className="text-slate-400 hover:text-slate-600 p-1 rounded hover:bg-slate-100 transition-colors cursor-pointer"
                        title="Cancel selection"
                      >
                        <X className="size-3.5" />
                      </button>
                    </div>

                    <div className="flex flex-wrap items-center justify-between gap-6">
                      <div className="flex items-center gap-2 flex-1 max-w-xs min-w-[200px]">
                        <Label className="text-[10px] font-bold uppercase tracking-wider text-[#5d3f3d] shrink-0">
                          Caption
                        </Label>
                        <Input
                          placeholder="e.g. Front View"
                          value={updateCaption}
                          onChange={(e) => setUpdateCaption(e.target.value)}
                          className={inputCls}
                        />
                      </div>

                      <div className="flex items-center gap-2 w-32 shrink-0">
                        <Label className="text-[10px] font-bold uppercase tracking-wider text-[#5d3f3d] shrink-0">
                          Order
                        </Label>
                        <Input
                          type="number"
                          placeholder="e.g. 1"
                          value={updateOrder}
                          onChange={(e) => setUpdateOrder(e.target.value === "" ? "" : Number(e.target.value))}
                          className={`${inputCls} [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                        />
                      </div>

                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          type="button"
                          onClick={handleSaveUpdateImage}
                          disabled={updateImageMutation.isPending}
                          className="px-4 py-1.5 bg-[#be001e] hover:bg-[#a50019] text-white font-bold text-xs rounded-lg transition-all flex items-center gap-1.5 cursor-pointer disabled:opacity-50 shadow-sm"
                        >
                          {updateImageMutation.isPending ? "Updating..." : "Update Detail"}
                        </button>
                      </div>
                    </div>

                    {updateSuccessMsg && (
                      <p className="text-xs text-emerald-600 font-semibold flex items-center gap-1">
                        <CheckCircle className="size-3.5" /> {updateSuccessMsg}
                      </p>
                    )}

                    {updateImageMutation.isError && (
                      <p className="text-xs text-[#be001e] font-semibold flex items-center gap-1">
                        <AlertCircle className="size-3.5" /> {(updateImageMutation.error as Error).message || "Failed to update image"}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Upload Inline Form */}
            {showUploadImageForm && (
              <div className="bg-white border-[0.5px] border-slate-200/80 rounded-xl p-4 space-y-3 shadow-sm animate-in fade-in duration-200">
                <div className="flex items-center justify-between">
                  <h4 className="text-[10px] font-extrabold uppercase tracking-wider text-[#5d3f3d]">Upload New Image</h4>
                  <button
                    type="button"
                    onClick={() => {
                      setShowUploadImageForm(false)
                      setSelectedFile(null)
                      setUploadSuccessMsg(null)
                    }}
                    className="text-slate-400 hover:text-slate-600 p-1 rounded hover:bg-slate-100 transition-colors cursor-pointer"
                    title="Close form"
                  >
                    <X className="size-3.5" />
                  </button>
                </div>

                <div className="flex flex-wrap items-center justify-between gap-6">
                  <div className="flex items-center gap-2 shrink-0">
                    <Label className="text-[10px] font-bold uppercase tracking-wider text-[#5d3f3d] shrink-0">
                      Select Image *
                    </Label>
                    <label className="inline-flex items-center gap-1.5 px-2 py-1 bg-[#fff0ef] text-[#be001e] border border-[#e7bdb9]/50 hover:bg-[#be001e]/10 rounded-lg text-xs font-bold cursor-pointer transition-colors shadow-sm shrink-0">
                      <Upload className="size-2" />
                      Choose File
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          setSelectedFile(e.target.files?.[0] || null)
                          setUploadSuccessMsg(null)
                        }}
                        className="hidden"
                      />
                    </label>
                  </div>

                  <div className="flex items-center gap-2 flex-1 max-w-xs min-w-[200px]">
                    <Label className="text-[10px] font-bold uppercase tracking-wider text-[#5d3f3d] shrink-0">
                      Caption
                    </Label>
                    <Input
                      placeholder="e.g. Front View"
                      value={imageCaption}
                      onChange={(e) => setImageCaption(e.target.value)}
                      className={inputCls}
                    />
                  </div>

                  <div className="flex items-center gap-2 w-32 shrink-0">
                    <Label className="text-[10px] font-bold uppercase tracking-wider text-[#5d3f3d] shrink-0">
                      Order
                    </Label>
                    <Input
                      type="number"
                      placeholder="e.g. 1"
                      value={imageOrder}
                      onChange={(e) => setImageOrder(e.target.value === "" ? "" : Number(e.target.value))}
                      className={`${inputCls} [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                    />
                  </div>
                </div>

                {/* Local File Preview */}
                {selectedFile && (
                  <div className="flex items-center justify-between p-2.5 bg-slate-50 border border-slate-200/60 rounded-lg">
                    <div className="flex items-center gap-3 min-w-0">
                      <img
                        src={URL.createObjectURL(selectedFile)}
                        alt="Preview"
                        className="w-12 h-12 rounded object-cover border border-slate-200 shrink-0"
                      />
                      <div className="min-w-0 text-xs">
                        <p className="font-bold text-slate-700 truncate">{selectedFile.name}</p>
                        <p className="text-[10px] text-slate-400">{(selectedFile.size / 1024).toFixed(1)} KB</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setSelectedFile(null)}
                      className="p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors cursor-pointer shrink-0"
                      title="Remove selected file"
                    >
                      <X className="size-4" />
                    </button>
                  </div>
                )}

                {uploadSuccessMsg && (
                  <p className="text-xs text-emerald-600 font-semibold flex items-center gap-1">
                    <CheckCircle className="size-3.5" /> {uploadSuccessMsg}
                  </p>
                )}

                {uploadImageMutation.isError && (
                  <p className="text-xs text-[#be001e] font-semibold flex items-center gap-1">
                    <AlertCircle className="size-3.5" /> {(uploadImageMutation.error as Error).message || "Failed to upload image"}
                  </p>
                )}

                <div className="flex justify-end pt-1">
                  <button
                    type="button"
                    onClick={handleUploadImage}
                    disabled={!selectedFile || uploadImageMutation.isPending}
                    className="px-4 py-1.5 bg-[#be001e] hover:bg-[#a50019] text-white font-bold text-xs rounded-lg transition-all flex items-center gap-1.5 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                  >
                    {uploadImageMutation.isPending ? <Loader2 className="size-3.5 animate-spin" /> : <Upload className="size-3.5" />}
                    {uploadImageMutation.isPending ? "Uploading..." : "Upload Image"}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="pt-4 border-t-[0.5px] border-[#be001e1a] flex items-center justify-end gap-3 p-2">
          <button type="button" onClick={onClose} className="px-5 py-2 text-slate-500 font-semibold rounded-lg text-xs hover:bg-slate-50 cursor-pointer">
            Cancel
          </button>
          <Button
            type="submit"
            disabled={isPending}
            className="px-6 py-2 bg-[#e6202f] hover:bg-[#c91c29] text-white font-bold rounded-lg text-xs transition-all cursor-pointer"
          >
            {isPending
              ? <span className="flex items-center gap-2"><Loader2 className="size-3.5 animate-spin" /> Saving…</span>
              : productToEdit ? "Update Product" : "Save Product"}
          </Button>
        </div>
      </form>
    </section>
  )
}
